/**
 * Riwayat event Field Task — MURNI.
 *
 * Satu aturan yang menentukan seluruh modul: **urutan ditentukan `sequence_no`, bukan waktu.**
 *
 * `occurred_at_device` berasal dari jam perangkat dan tidak dipercaya sama sekali;
 * `occurred_at_server` pun bisa menghasilkan dua event bernilai sama persis ketika dua transisi
 * terjadi dalam milidetik yang sama. `sequence_no` adalah satu-satunya urutan yang server jamin
 * monoton per tugas (ADR-0035, `field_task_events`). Mengurutkan riwayat menurut waktu akan
 * menampilkan "sampai lokasi" sebelum "mulai" pada tugas yang transisinya cepat — dan yang
 * membaca riwayat itu justru orang yang sedang menyelidiki apa yang terjadi.
 */

export interface TaskEventLite {
  readonly id: string;
  readonly sequenceNo: number;
  readonly eventType: string;
  readonly occurredAtServer: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly accuracyMeters: number | null;
  readonly distanceToDestinationMeters: number | null;
  readonly impliedSpeedKmh: number | null;
  readonly note: string | null;
}

/**
 * Ambang kecurigaan lompatan lokasi — HARUS SAMA dengan `AMBANG_KECEPATAN_MUSTAHIL_KMH` di
 * `apps/workforce-api/src/modules/field-task/field-task-location.ts`. Server yang menghitung
 * `impliedSpeedKmh`; ini cuma menentukan kapan angkanya ditampilkan sebagai peringatan, bukan
 * sumber kebenaran baru — geser satu, ingat geser yang lain.
 */
export const AMBANG_KECEPATAN_MUSTAHIL_KMH = 150;

/** `null` berarti tidak ada dasar pembanding (event lokasi pertama aktor, atau jeda terlalu singkat) — bukan kecurigaan. */
export function lompatanMencurigakan(impliedSpeedKmh: number | null): boolean {
  return impliedSpeedKmh !== null && impliedSpeedKmh > AMBANG_KECEPATAN_MUSTAHIL_KMH;
}

/** Menaik menurut `sequence_no` — urutan kejadian, bukan urutan jam. */
export function sortEvents<E extends TaskEventLite>(events: readonly E[]): readonly E[] {
  return [...events].sort((a, b) => a.sequenceNo - b.sequenceNo);
}

/**
 * Event yang punya titik lokasi.
 *
 * Dipakai untuk menampilkan TITIK-TITIK kejadian (mulai, sampai), **bukan jejak perjalanan**.
 * ADR-0036 §D1 melarang pelacakan berkelanjutan: yang disimpan hanyalah lokasi pada saat
 * transisi, dan menampilkannya sebagai garis akan menyiratkan data yang tidak pernah kita
 * kumpulkan — sekaligus menjanjikan pengawasan yang sengaja tidak dibangun.
 */
export function eventsWithLocation<E extends TaskEventLite>(events: readonly E[]): readonly E[] {
  return sortEvents(events).filter((e) => e.latitude !== null && e.longitude !== null);
}

const EVENT_LABEL: Readonly<Record<string, string>> = {
  created: 'Dibuat',
  assigned: 'Ditugaskan',
  reassigned: 'Dialihkan',
  accepted: 'Diterima',
  started: 'Mulai dikerjakan',
  arrived: 'Sampai di lokasi',
  completed: 'Selesai',
  failed: 'Gagal',
  cancelled: 'Dibatalkan',
  note_added: 'Catatan ditambahkan',
};

export function formatEventType(eventType: string): string {
  return EVENT_LABEL[eventType] ?? eventType;
}

/**
 * Deep link navigasi ke tujuan.
 *
 * Memakai koordinat bila ada, alamat bila tidak. Koordinat lebih tepat; alamat lebih mudah
 * dibaca manusia tetapi bisa gagal dicocokkan oleh peta di daerah yang pemetaannya buruk —
 * dan itu justru daerah tempat kurir paling butuh navigasi.
 */
export function navigationLinks(destination: {
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly address: string | null;
}): { readonly google: string | null; readonly waze: string | null } {
  const { latitude, longitude, address } = destination;

  if (latitude !== null && longitude !== null) {
    return {
      google: `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`,
      waze: `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`,
    };
  }
  if (address !== null && address.trim().length > 0) {
    const q = encodeURIComponent(address.trim());
    return {
      google: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
      // Waze tidak punya parameter alamat yang setara andal; tanpa koordinat lebih jujur
      // tidak menawarkan tautannya daripada membuka aplikasi ke tempat yang salah.
      waze: null,
    };
  }
  return { google: null, waze: null };
}

const PRIORITY_ORDER: Readonly<Record<string, number>> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const PRIORITY_LABEL: Readonly<Record<string, string>> = {
  urgent: 'Mendesak',
  high: 'Tinggi',
  medium: 'Sedang',
  low: 'Rendah',
};

export function formatPriority(priority: string): string {
  return PRIORITY_LABEL[priority] ?? priority;
}

export interface SortableTask {
  readonly priority: string;
  readonly scheduledWindowStart: string | null;
}

/**
 * Urutan daftar tugas: prioritas dulu, lalu jadwal paling awal.
 *
 * Tugas tanpa jadwal diletakkan SETELAH yang berjadwal pada prioritas sama — bukan sebelumnya.
 * Yang punya jendela waktu punya batas nyata; yang tanpa jadwal bisa dikerjakan kapan saja.
 */
export function sortTasks<T extends SortableTask>(tasks: readonly T[]): readonly T[] {
  return [...tasks].sort((a, b) => {
    const byPriority = (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99);
    if (byPriority !== 0) return byPriority;

    if (a.scheduledWindowStart === null && b.scheduledWindowStart === null) return 0;
    if (a.scheduledWindowStart === null) return 1;
    if (b.scheduledWindowStart === null) return -1;
    return a.scheduledWindowStart < b.scheduledWindowStart ? -1 : 1;
  });
}
